/**
 * Sex filter for the AYAQ main search page.
 * Mirrors ayaq-search-panel: hide products by data-product-gender after
 * storefront facets update, because search tag filters alone are unreliable.
 * Also syncs checkbox / "X sélectionné" / active-facet pills from the URL,
 * since Shopify often leaves filter.p.tag sex values inactive on search.
 */
(() => {
  const SEX_VALUES = ['homme', 'femme', 'unisex'];
  const PILL_ATTR = 'data-ayaq-sex-pill';

  const normalizeSex = (value) => {
    const raw = String(value || '').toLowerCase().trim();

    if (!raw) return null;

    for (const sex of SEX_VALUES) {
      if (raw === sex || raw.includes(sex)) return sex;
    }

    return null;
  };

  const getActiveSexFromUrl = () => {
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of params.entries()) {
      const keyLower = key.toLowerCase();
      const isSexParam =
        keyLower.includes('filter.p.tag') ||
        keyLower.includes('genre') ||
        keyLower.includes('sexe') ||
        keyLower.includes('gender');

      if (!isSexParam) continue;

      const sex = normalizeSex(value);

      if (sex) return sex;
    }

    return null;
  };

  const getActiveSexFromInputs = () => {
    for (const root of document.querySelectorAll('[data-ayaq-sex-filter]')) {
      for (const input of root.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')) {
        const sex = normalizeSex(input.value) || normalizeSex(input.id);

        if (sex) return sex;
      }
    }

    return null;
  };

  const buildUrlWithoutSex = () => {
    const params = new URLSearchParams(window.location.search);
    const next = new URLSearchParams();

    for (const [key, value] of params.entries()) {
      const keyLower = key.toLowerCase();
      const isTagParam = keyLower.includes('filter.p.tag');

      if (isTagParam && normalizeSex(value)) continue;

      next.append(key, value);
    }

    const qs = next.toString();

    return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  };

  const getSexLabel = (sex) => {
    if (!sex) return '';

    for (const input of document.querySelectorAll('[data-ayaq-sex-filter] input[type="checkbox"]')) {
      if (normalizeSex(input.value) !== sex) continue;

      const label = input.closest('label');
      const text = (label?.querySelector('[aria-hidden="true"]') || label)?.textContent || '';
      const cleaned = text.replace(/\(\s*\d+\s*\)/g, '').replace(/\s+/g, ' ').trim();

      if (cleaned) return cleaned;
    }

    return sex.charAt(0).toUpperCase() + sex.slice(1);
  };

  const getSexGroupLabel = () => {
    for (const root of document.querySelectorAll('[data-ayaq-sex-filter]')) {
      const summaryText = root.querySelector('.facets__summary span, .mobile-facets__summary span')?.childNodes;

      if (summaryText) {
        for (const node of summaryText) {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.replace(/\s+/g, ' ').trim();

            if (text) return text;
          }
        }
      }

      const legend = root.querySelector('legend')?.textContent?.trim();

      if (legend) return legend;
    }

    return 'Sexe';
  };

  const syncSexInputs = (sex) => {
    for (const root of document.querySelectorAll('[data-ayaq-sex-filter]')) {
      for (const input of root.querySelectorAll('input[type="checkbox"]')) {
        const matches = Boolean(sex) && normalizeSex(input.value) === sex;

        input.checked = matches;
      }

      const count = sex ? 1 : 0;
      const selected = root.querySelector('.facets__selected');

      if (selected) {
        if (selected.classList.contains('hidden') || selected.textContent.includes('(')) {
          selected.textContent = ` (${count})`;
          selected.classList.toggle('hidden', count === 0);
        } else {
          selected.textContent = selected.textContent.replace(/\d+/g, String(count));
        }
      }

      const mobileSelected = root.querySelector('.mobile-facets__selected');

      if (mobileSelected) {
        mobileSelected.textContent = String(count);
        mobileSelected.hidden = count === 0;
      }

      const summary = root.querySelector('.facets__summary, .mobile-facets__summary');

      if (summary?.hasAttribute('aria-label')) {
        const label = summary.getAttribute('aria-label') || '';
        const updated = label.replace(/\d+/g, String(count));

        summary.setAttribute('aria-label', updated);
      }
    }
  };

  const syncSexPills = (sex) => {
    const containers = document.querySelectorAll('.active-facets-desktop, .active-facets-mobile');

    for (const container of containers) {
      for (const old of container.querySelectorAll(`[${PILL_ATTR}]`)) {
        old.remove();
      }

      if (!sex) continue;

      const groupLabel = getSexGroupLabel();
      const valueLabel = getSexLabel(sex);
      const clearUrl = buildUrlWithoutSex();

      const existingIcon =
        container.querySelector('.active-facets__button-inner .icon-close-small')?.outerHTML ||
        `<svg aria-hidden="true" focusable="false" role="presentation" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;

      const wrap = document.createElement('facet-remove');
      wrap.setAttribute(PILL_ATTR, sex);
      wrap.innerHTML = `
        <a href="${clearUrl}" class="active-facets__button active-facets__button--light js-facet-remove">
          <span class="active-facets__button-inner button button--tertiary">
            ${groupLabel}: ${valueLabel}
            ${existingIcon}
            <span class="visually-hidden">${groupLabel}</span>
          </span>
        </a>
      `;

      const clearAll = container.querySelector('.active-facets__button-wrapper, facet-remove.active-facets__button-wrapper');

      if (clearAll) {
        container.insertBefore(wrap, clearAll);
      } else {
        container.prepend(wrap);
      }
    }
  };

  const countNonSexFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    let count = 0;
    let priceCounted = false;

    for (const [key, value] of params.entries()) {
      if (!key.startsWith('filter.')) continue;

      const keyLower = key.toLowerCase();

      if (
        (keyLower.includes('filter.p.tag') ||
          keyLower.includes('genre') ||
          keyLower.includes('sexe') ||
          keyLower.includes('gender')) &&
        normalizeSex(value)
      ) {
        continue;
      }

      if (keyLower.includes('price')) {
        if (!priceCounted) {
          count += 1;
          priceCounted = true;
        }
        continue;
      }

      count += 1;
    }

    return count;
  };

  const syncOpenCount = (sex) => {
    const open = document.querySelector('.mobile-facets__open');

    if (!open) return;

    const count = countNonSexFiltersFromUrl() + (sex ? 1 : 0);
    let countEl = open.querySelector('.mobile-facets__open-count');
    let srEl = open.querySelector(':scope > .visually-hidden');

    if (count > 0) {
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = 'mobile-facets__open-count';
        countEl.setAttribute('aria-hidden', 'true');
        open.appendChild(countEl);
      }

      countEl.textContent = String(count);

      if (!srEl) {
        srEl = document.createElement('span');
        srEl.className = 'visually-hidden';
        open.appendChild(srEl);
      }

      srEl.textContent = `${count} sélectionné`;
    } else {
      countEl?.remove();
      srEl?.remove();
    }
  };

  const applySexFilter = () => {
    const sex = getActiveSexFromUrl() || getActiveSexFromInputs();
    const nodes = document.querySelectorAll('[data-product-gender]');

    syncSexInputs(sex);
    syncSexPills(sex);
    syncOpenCount(sex);

    if (!nodes.length) return sex;

    for (const node of nodes) {
      const matches = !sex || node.dataset.productGender === sex;

      node.hidden = !matches;
    }

    for (const divider of document.querySelectorAll('.ayaq-models-list > .ayaq-model-divider')) {
      const previous = divider.previousElementSibling;
      const next = divider.nextElementSibling;
      const prevVisible = previous && previous.classList.contains('ayaq-model-section') && !previous.hidden;
      const nextVisible = next && next.classList.contains('ayaq-model-section') && !next.hidden;

      divider.hidden = !(prevVisible && nextVisible);
    }

    const overview = document.querySelector('.ayaq-models-overview');

    if (overview) {
      const cards = overview.querySelectorAll('[data-product-gender]');
      let visibleCards = 0;

      for (const card of cards) {
        if (!card.hidden) visibleCards += 1;
      }

      overview.hidden = Boolean(sex) && visibleCards === 0;
    }

    return sex;
  };

  const scheduleApply = () => {
    requestAnimationFrame(() => applySexFilter());
  };

  document.addEventListener('DOMContentLoaded', scheduleApply);
  window.addEventListener('popstate', scheduleApply);

  document.addEventListener('change', (event) => {
    const sexRoot = event.target.closest('[data-ayaq-sex-filter]');

    if (!sexRoot || !event.target.matches('input[type="checkbox"]')) return;

    if (event.target.checked) {
      for (const other of sexRoot.querySelectorAll('input[type="checkbox"]')) {
        if (other !== event.target) other.checked = false;
      }
    }

    scheduleApply();
  });

  const patchFacets = () => {
    // Class declarations are global lexical bindings, not always on `window`.
    if (typeof FacetFiltersForm === 'undefined' || FacetFiltersForm.__ayaqSexPatched) {
      return typeof FacetFiltersForm !== 'undefined';
    }

    const originalGrid = FacetFiltersForm.renderProductGridContainer;
    const originalFilters = FacetFiltersForm.renderFilters;
    const originalActive = FacetFiltersForm.renderActiveFacets;

    FacetFiltersForm.renderProductGridContainer = function patchedRenderProductGridContainer(html) {
      originalGrid.call(this, html);
      scheduleApply();
    };

    FacetFiltersForm.renderFilters = function patchedRenderFilters(html, event) {
      originalFilters.call(this, html, event);
      scheduleApply();
    };

    FacetFiltersForm.renderActiveFacets = function patchedRenderActiveFacets(html) {
      originalActive.call(this, html);
      scheduleApply();
    };

    const originalAdditional = FacetFiltersForm.renderAdditionalElements;

    FacetFiltersForm.renderAdditionalElements = function patchedRenderAdditionalElements(html) {
      originalAdditional.call(this, html);
      scheduleApply();
    };

    FacetFiltersForm.__ayaqSexPatched = true;
    scheduleApply();
    return true;
  };

  if (!patchFacets()) {
    document.addEventListener('DOMContentLoaded', patchFacets);
  }

  // Retry once scripts/custom elements finish registering.
  requestAnimationFrame(() => {
    patchFacets();
    scheduleApply();
  });
})();
